import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config, { REMOTE_API } from '../../config/request.config';

// ─── Types ────────────────────────────────────────────────────────────────────

type Tab = 'cadastros' | 'roleta' | 'sistema';

interface Registration {
  id: number;
  code: string;
  user_totem: string;
  lead_nome: string;
  lead_telefone: string;
  prize: string;
  created_at: string;
}

interface ReportData {
  total: number;
  byPrize: { prize: string; qty: number }[];
  byTotem: { user_totem: string; qty: number }[];
  byDate: { date: string; qty: number }[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SLOT_QTY_KEY = 'softys_roulette_slot_quantities';
const DAILY_KEY    = 'softys_totokinha_daily';
const SLOT_COLORS_KEY = 'softys_roulette_slot_colors';
const ORDER_KEY    = 'softys_roulette_order';
const ANIM_KEY     = 'softys_animations';

interface AnimConfig {
  typewriter:  boolean;
  logoReveal:  boolean;
  qrPulse:     boolean;
  textEffects: boolean;
}
const DEFAULT_ANIM: AnimConfig = { typewriter: true, logoReveal: true, qrPulse: true, textEffects: true };
function loadAdminAnimConfig(): AnimConfig {
  try { return { ...DEFAULT_ANIM, ...JSON.parse(localStorage.getItem(ANIM_KEY) || '{}') }; }
  catch { return DEFAULT_ANIM; }
}

/** 5 slots – one entry per prize, quantities totais. */
const INITIAL_SLOTS = [
  { id: 0, name: 'Sab. Pom Pom',      defaultColor: '#93D4DE', textColor: '#1a1a1a', initialQty: 1400, dailyLimit: false, dailyLimitQty: 0 },
  { id: 1, name: 'Sh. Pom Pom',        defaultColor: '#075D82', textColor: '#FFFFFF', initialQty: 240,  dailyLimit: false, dailyLimitQty: 0 },
  { id: 2, name: 'Lenço Elite',         defaultColor: '#6FC6D3', textColor: '#1a1a1a', initialQty: 1000, dailyLimit: false, dailyLimitQty: 0 },
  { id: 3, name: 'Totoka Babysec',      defaultColor: '#195DA9', textColor: '#FFFFFF', initialQty: 8,    dailyLimit: true,  dailyLimitQty: 1 },
  { id: 4, name: 'Desconto 15%',        defaultColor: '#5286BF', textColor: '#FFFFFF', initialQty: 1500, dailyLimit: false, dailyLimitQty: 0 },
];

const NUM_SLOTS = 5;
const DEFAULT_ORDER = [0, 1, 2, 3, 4];

const getTodayStr = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};

/** Prompt for a PIN before running a dangerous action */
const requirePin = (action: () => void) => {
  const pin = window.prompt('Digite a senha para continuar:');
  if (pin === null) return; // cancelled
  if (pin === '3100') {
    action();
  } else {
    alert('Senha incorreta!');
  }
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  page: {
    width: '100%', height: '100vh',
    backgroundColor: '#0f1923',
    color: '#FFFFFF',
    fontFamily: 'Arial, sans-serif',
    display: 'flex', flexDirection: 'column' as const,
    overflow: 'hidden' as const,
  },
  header: {
    backgroundColor: '#1a2740',
    padding: '28px 40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '3px solid #5b9bd5',
  },
  headerTitle: { fontSize: '42px', fontWeight: 'bold', color: '#5b9bd5' },
  backBtn: {
    background: 'transparent', border: '2px solid #5b9bd5',
    color: '#5b9bd5', borderRadius: '10px',
    padding: '14px 36px', fontSize: '32px', cursor: 'pointer',
  },
  tabs: {
    display: 'flex', borderBottom: '2px solid #2a3a50',
    backgroundColor: '#152030',
  },
  tab: (active: boolean) => ({
    padding: '24px 48px', fontSize: '34px', fontWeight: 'bold',
    cursor: 'pointer', border: 'none', background: 'transparent',
    color: active ? '#5b9bd5' : '#8899aa',
    borderBottom: active ? '4px solid #5b9bd5' : '4px solid transparent',
    transition: 'all 0.2s',
  }),
  content: { padding: '36px 44px', flex: 1, overflowY: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '26px' },
  th: { backgroundColor: '#1a2740', padding: '18px 20px', textAlign: 'left' as const, color: '#5b9bd5', borderBottom: '2px solid #2a3a50' },
  td: { padding: '16px 20px', borderBottom: '1px solid #1e2e3e', verticalAlign: 'middle' as const },
  btn: (color = '#5b9bd5') => ({
    backgroundColor: color, color: '#fff', border: 'none',
    borderRadius: '10px', padding: '16px 36px', fontSize: '28px',
    cursor: 'pointer', fontWeight: 'bold',
  }),
  badge: (color: string) => ({
    display: 'inline-block', backgroundColor: color,
    borderRadius: '8px', padding: '4px 14px', fontSize: '22px', fontWeight: 'bold'
  }),
  sectionTitle: { fontSize: '36px', fontWeight: 'bold', color: '#5b9bd5', margin: '30px 0 16px' },
  card: {
    backgroundColor: '#1a2740', borderRadius: '14px',
    padding: '28px 32px', marginBottom: '24px',
  },
  infoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #2a3a50' },
};

// ─── Tab 1 – Cadastros ────────────────────────────────────────────────────────

const TabCadastros: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Registration[]>([]);
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Offline queue count
  const pendingCount = (() => {
    try {
      const q = JSON.parse(localStorage.getItem('softys_pending_sync') || '[]');
      return q.filter((r: any) => !r.synced).length;
    } catch { return 0; }
  })();

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [resAll, resReport] = await Promise.all([
        axios.get(`${REMOTE_API}/v1/subscription/all`, { timeout: 5000 }),
        axios.get(`${REMOTE_API}/v1/subscription/report`, { timeout: 5000 }),
      ]);
      setRows(resAll.data || []);
      setReport(resReport.data);
    } catch {
      setError('Servidor offline. Mostrando dados do localStorage.');
      try {
        const q = JSON.parse(localStorage.getItem('softys_pending_sync') || '[]');
        const mapped: Registration[] = q.map((r: any, i: number) => ({
          id: i + 1,
          code: r.data?.oxCustom?.code || '',
          user_totem: r.data?.oxCustom?.user || '',
          lead_nome: r.data?.oxCustom?.lead?.NOME || '',
          lead_telefone: r.data?.oxCustom?.lead?.TELEFONE || '',
          prize: r.data?.oxCustom?.roulette?.prize || '',
          created_at: new Date(r.timestamp).toLocaleString('pt-BR'),
        }));
        setRows(mapped);
      } catch {}
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r =>
    [r.lead_nome, r.lead_telefone, r.prize, r.user_totem]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '32px', flexWrap: 'wrap' }}>
        {[
          { label: 'Total de cadastros', value: report?.total ?? rows.length, color: '#5b9bd5' },
          { label: 'Aguardando sync', value: pendingCount, color: pendingCount > 0 ? '#e67e22' : '#27ae60' },
          ...(report?.byPrize ?? []).map(p => ({ label: p.prize, value: `${p.qty}×`, color: '#195DA9' })),
        ].map((card, i) => (
          <div key={i} style={{ ...S.card, minWidth: '200px', flex: '1', textAlign: 'center' }}>
            <div style={{ fontSize: '56px', fontWeight: 'bold', color: card.color }}>{card.value}</div>
            <div style={{ fontSize: '24px', color: '#aab', marginTop: '6px' }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍  Buscar por nome, telefone, prêmio..."
          style={{ flex: 1, minWidth: '300px', padding: '16px 20px', fontSize: '28px', borderRadius: '10px', border: '2px solid #2a3a50', backgroundColor: '#0f1923', color: '#fff' }}
        />
        <button style={S.btn()} onClick={load}>↻ Atualizar</button>
        <a
          href={`${REMOTE_API}/v1/subscription/export/csv`}
          target="_blank" rel="noreferrer"
          style={{ ...S.btn('#27ae60'), textDecoration: 'none', display: 'inline-block', lineHeight: 'normal' }}
        >
          ⬇ Exportar CSV
        </a>
        <button style={S.btn('#195DA9')} onClick={() => navigate('/synced-registrations')}>
          📋 Ver Servidor Remoto
        </button>
      </div>

      {error && <div style={{ color: '#e67e22', fontWeight: 'bold', marginBottom: '16px', fontSize: '26px' }}>⚠ {error}</div>}
      {loading ? (
        <div style={{ textAlign: 'center', fontSize: '36px', padding: '60px', color: '#5b9bd5' }}>Carregando...</div>
      ) : (
        <>
          <div style={{ fontSize: '24px', color: '#aab', marginBottom: '12px' }}>
            {filtered.length} de {rows.length} registros
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  {['#', 'Nome', 'Telefone', 'Prêmio', 'Data'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={5} style={{ ...S.td, textAlign: 'center', color: '#aab', fontSize: '28px', padding: '40px' }}>Nenhum registro encontrado</td></tr>
                ) : filtered.map(r => (
                  <tr key={r.id} style={{ backgroundColor: r.id % 2 === 0 ? '#101e2c' : 'transparent' }}>
                    <td style={{ ...S.td, color: '#5b9bd5', fontWeight: 'bold' }}>{r.id}</td>
                    <td style={S.td}>{r.lead_nome || '–'}</td>
                    <td style={S.td}>{r.lead_telefone || '–'}</td>
                    <td style={S.td}>
                      <span style={S.badge(
                        INITIAL_SLOTS.find(p => p.name === r.prize)?.defaultColor || '#2a3a50'
                      )}>{r.prize || '–'}</span>
                    </td>
                    <td style={{ ...S.td, color: '#aab', fontSize: '22px' }}>{r.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Tab 2 – Configuração da Roleta ──────────────────────────────────────────

const TabRoleta: React.FC = () => {
  const [slotQuantities, setSlotQuantities] = useState<Record<number, number>>({});
  const [slotColors, setSlotColors] = useState<Record<number, string>>({});
  const [editingQty, setEditingQty] = useState<Record<number, string>>({});
  const [editingColor, setEditingColor] = useState<Record<number, string>>({});
  const [order, setOrder] = useState<number[]>([...DEFAULT_ORDER]);
  const [saved, setSaved] = useState('');
  const [showRules, setShowRules] = useState(false);

  const todayDaily = (() => {
    try {
      const d = JSON.parse(localStorage.getItem(DAILY_KEY) || '{}');
      return d.date === getTodayStr() ? 'Já sorteada hoje' : 'Disponível hoje';
    } catch { return '–'; }
  })();

  useEffect(() => {
    const storedQty: Record<string, number> = JSON.parse(localStorage.getItem(SLOT_QTY_KEY) || '{}');
    const storedColors: Record<string, string> = JSON.parse(localStorage.getItem(SLOT_COLORS_KEY) || '{}');
    const storedOrder: number[] | null = JSON.parse(localStorage.getItem(ORDER_KEY) || 'null');

    const initQty: Record<number, number> = {};
    const initColors: Record<number, string> = {};
    INITIAL_SLOTS.forEach(s => {
      initQty[s.id] = storedQty[String(s.id)] ?? s.initialQty;
      initColors[s.id] = storedColors[String(s.id)] ?? s.defaultColor;
    });
    setSlotQuantities(initQty);
    setSlotColors(initColors);
    setEditingQty(Object.fromEntries(INITIAL_SLOTS.map(s => [s.id, String(initQty[s.id])])));
    setEditingColor(Object.fromEntries(INITIAL_SLOTS.map(s => [s.id, initColors[s.id]])));
    setOrder(storedOrder && storedOrder.length === NUM_SLOTS ? storedOrder : [...DEFAULT_ORDER]);
  }, []);

  const saveAll = () => {
    const newQty: Record<number, number> = {};
    INITIAL_SLOTS.forEach(s => {
      newQty[s.id] = parseInt(editingQty[s.id] ?? '0') || 0;
    });
    localStorage.setItem(SLOT_QTY_KEY, JSON.stringify(newQty));
    localStorage.setItem(SLOT_COLORS_KEY, JSON.stringify(editingColor));
    localStorage.setItem(ORDER_KEY, JSON.stringify(order));
    setSlotQuantities(newQty);
    setSlotColors(editingColor);
    setSaved('✔ Salvo com sucesso!');
    setTimeout(() => setSaved(''), 3000);
  };

  const handlePositionChange = (currentVisualPos: number, newVisualPos: number) => {
    if (currentVisualPos === newVisualPos) return;
    const newOrder = [...order];
    // Swap the two slots
    const temp = newOrder[currentVisualPos];
    newOrder[currentVisualPos] = newOrder[newVisualPos];
    newOrder[newVisualPos] = temp;
    setOrder(newOrder);
  };

  const resetQuantities = () => {
    const initQty: Record<number, string> = {};
    INITIAL_SLOTS.forEach(s => { initQty[s.id] = String(s.initialQty); });
    setEditingQty(initQty);
  };

  const resetOrder = () => {
    setOrder([...DEFAULT_ORDER]);
  };

  const resetTotokinha = () => {
    localStorage.removeItem(DAILY_KEY);
    setSaved('✔ Bloqueio diário da Totokinha removido!');
    setTimeout(() => setSaved(''), 3000);
  };

  return (
    <div>
      {saved && (
        <div style={{ backgroundColor: '#1a4a2e', border: '2px solid #27ae60', borderRadius: '10px', padding: '18px 28px', marginBottom: '24px', fontSize: '30px', color: '#27ae60' }}>
          {saved}
        </div>
      )}

      {/* Prize table – 10 slots */}
      <div style={S.sectionTitle}>🎡 Brindes e Posições (10 slots da roleta)</div>
      <div style={{ fontSize: '24px', color: '#7799bb', marginBottom: '16px', lineHeight: 1.5 }}>
        Use o dropdown de <strong>Posição</strong> para reorganizar os itens. A ordem aqui é a mesma exibida na roleta.
      </div>
      <div style={{ overflowX: 'auto', marginBottom: '32px' }}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: '120px' }}>Posição</th>
              <th style={S.th}>Brinde</th>
              <th style={S.th}>Cor do Setor</th>
              <th style={{ ...S.th, width: '160px' }}>Qtd. Inicial</th>
              <th style={{ ...S.th, width: '180px' }}>Qtd. Restante</th>
              <th style={S.th}>Limite Diário</th>
              <th style={S.th}>Status Hoje</th>
            </tr>
          </thead>
          <tbody>
            {order.map((slotId, visualPos) => {
              const slot = INITIAL_SLOTS[slotId];
              return (
                <tr key={`slot-${slotId}-${visualPos}`} style={{ backgroundColor: visualPos % 2 === 0 ? '#101e2c' : 'transparent' }}>
                  {/* Position dropdown */}
                  <td style={S.td}>
                    <select
                      value={visualPos}
                      onChange={e => handlePositionChange(visualPos, parseInt(e.target.value))}
                      style={{
                        width: '100px', padding: '10px 8px', fontSize: '28px', fontWeight: 'bold',
                        borderRadius: '8px', border: '2px solid #5b9bd5', backgroundColor: '#0f1923',
                        color: '#5b9bd5', textAlign: 'center', cursor: 'pointer',
                      }}
                    >
                      {order.map((_, pos) => (
                        <option key={pos} value={pos}>{pos + 1}º</option>
                      ))}
                    </select>
                  </td>
                  {/* Name */}
                  <td style={{ ...S.td, fontWeight: 'bold', fontSize: '28px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: editingColor[slotId] || slot.defaultColor, flexShrink: 0, border: '2px solid #fff' }} />
                      {slot.name}
                    </div>
                  </td>
                  {/* Color */}
                  <td style={S.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input
                        type="color"
                        value={editingColor[slotId] || slot.defaultColor}
                        onChange={e => setEditingColor(prev => ({ ...prev, [slotId]: e.target.value }))}
                        style={{ width: '60px', height: '50px', cursor: 'pointer', border: 'none', borderRadius: '6px', backgroundColor: 'transparent' }}
                      />
                      <span style={{ fontSize: '22px', color: '#aab' }}>{editingColor[slotId] || slot.defaultColor}</span>
                    </div>
                  </td>
                  {/* Initial qty */}
                  <td style={{ ...S.td, color: '#aab' }}>{slot.initialQty.toLocaleString('pt-BR')}</td>
                  {/* Remaining qty */}
                  <td style={S.td}>
                    <input
                      type="number"
                      value={editingQty[slotId] ?? ''}
                      onChange={e => setEditingQty(prev => ({ ...prev, [slotId]: e.target.value }))}
                      style={{ width: '140px', padding: '10px 14px', fontSize: '28px', borderRadius: '8px', border: '2px solid #2a3a50', backgroundColor: '#0f1923', color: '#fff', textAlign: 'center' }}
                      min={0}
                    />
                  </td>
                  {/* Daily limit */}
                  <td style={S.td}>
                    {slot.dailyLimit ? (
                      <span style={S.badge('#e67e22')}>⚠ {slot.dailyLimitQty}× por dia</span>
                    ) : (
                      <span style={{ color: '#aab', fontSize: '24px' }}>Sem limite</span>
                    )}
                  </td>
                  {/* Status */}
                  <td style={S.td}>
                    {slot.dailyLimit ? (
                      <div>
                        <span style={S.badge(todayDaily.includes('Já') ? '#c0392b' : '#27ae60')}>
                          {todayDaily}
                        </span>
                        {todayDaily.includes('Já') && (
                          <button onClick={resetTotokinha} style={{ ...S.btn('#e67e22'), marginLeft: '12px', padding: '8px 18px', fontSize: '22px' }}>
                            ↺ Liberar
                          </button>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#27ae60', fontSize: '24px' }}>✓ Livre</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '40px' }}>
        <button style={S.btn('#5b9bd5')} onClick={saveAll}>💾 Salvar alterações</button>
        <button style={S.btn('#e67e22')} onClick={() => requirePin(resetQuantities)}>↺ Restaurar quantidades iniciais</button>
        <button style={S.btn('#8e44ad')} onClick={() => requirePin(resetOrder)}>↺ Restaurar ordem padrão</button>
        <button style={S.btn('#c0392b')} onClick={() => requirePin(async () => {
          if (window.confirm(
            'ATENÇÃO: Esta ação irá apagar TODOS os dados:\n\n' +
            '• Quantidades no localStorage (totem)\n' +
            '• Fila de sincronização pendente\n' +
            '• Base de dados remota (servidor na nuvem)\n\n' +
            'Isso é irreversível! Deseja continuar?'
          )) {
            // Limpa localStorage
            localStorage.removeItem(SLOT_QTY_KEY);
            localStorage.removeItem('softys_pending_sync');
            resetQuantities();
            // Limpa base remota
            try {
              await axios.delete(`${REMOTE_API}/v1/subscription/all`, { timeout: 8000 });
              setSaved('✔ localStorage e base remota zerados! Recarregando...');
              setTimeout(() => window.location.reload(), 1500);
            } catch {
              setSaved('✔ localStorage zerado. Falha ao limpar servidor remoto (offline?).');
              setTimeout(() => setSaved(''), 5000);
            }
          }
        })}>🗑 Zerar localStorage + Servidor</button>
      </div>

      {/* Rules section */}
      <div style={S.sectionTitle}>
        📋 Regras da Roleta
        <button
          onClick={() => setShowRules(r => !r)}
          style={{ ...S.btn('#1a2740'), marginLeft: '20px', padding: '10px 26px', fontSize: '26px', border: '2px solid #5b9bd5' }}
        >
          {showRules ? '▲ Ocultar' : '▼ Ver regras'}
        </button>
      </div>
      {showRules && (
        <div style={{ ...S.card, fontSize: '28px', lineHeight: 1.8 }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#5b9bd5', marginBottom: '20px' }}>
            Regras de Sorteio — EXPODIRETO Softys
          </div>
          <ul style={{ paddingLeft: '32px', color: '#dde' }}>
            <li>A roleta exibe <strong>5 posições</strong>. A ordem das posições pode ser ajustada pelo admin.</li>
            <li>Por padrão o sorteio dos prêmios é <strong>aleatório (randômico)</strong>.</li>
            <li>Cada participante gira a roleta <strong>uma única vez</strong> e ganha o prêmio sorteado.</li>
            <li><strong>Totoka Babysec</strong> tem limite de <strong>1 sorteio por dia</strong>. Após ser sorteada em um dia, ela fica bloqueada até o dia seguinte e outro brinde é sorteado em seu lugar.</li>
            <li>Quando a <strong>quantidade de um brinde é esgotada</strong>, o setor <strong>desaparece completamente da roleta</strong> — não fica visível, não fica desativado, é removido.</li>
            <li><strong>Não há programação diária de quantidades</strong> por brinde — o controle é somente pelo total de cada brinde.</li>
          </ul>
          <div style={{ marginTop: '24px', backgroundColor: '#1a2740', borderRadius: '10px', padding: '20px', border: '2px solid #e67e22' }}>
            <div style={{ color: '#e67e22', fontWeight: 'bold', fontSize: '28px', marginBottom: '10px' }}>⚠ Importante</div>
            <div>Quando a quantidade de um brinde for zerada, o setor <strong>desaparece automaticamente da roleta</strong>. Ele não fica visível nem desativado — é removido completamente até que um administrador reponha a quantidade.</div>
          </div>
          <div style={{ marginTop: '24px', backgroundColor: '#1a2740', borderRadius: '10px', padding: '20px', border: '2px solid #f39c12' }}>
            <div style={{ color: '#f39c12', fontWeight: 'bold', fontSize: '28px', marginBottom: '10px' }}>🌟 Único com limite diário</div>
            <div><strong>Totoka Babysec</strong> — máximo <strong>1 sorteio por dia</strong> (das 8 unidades totais, apenas 1 pode ser sorteada por dia. Quando a quantidade total zerar, ela desaparece da roleta).</div>
          </div>

          <div style={{ marginTop: '28px' }}>
            <div style={{ fontSize: '30px', fontWeight: 'bold', color: '#5b9bd5', marginBottom: '16px' }}>Cores dos setores da roleta:</div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {INITIAL_SLOTS.map(s => (
                <div key={`color-${s.id}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#0f1923', borderRadius: '8px', padding: '10px 16px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '6px', backgroundColor: s.defaultColor, border: '2px solid #fff' }} />
                  <span style={{ fontSize: '24px' }}>{s.name}</span>
                  <span style={{ fontSize: '20px', color: '#aab' }}>{s.defaultColor}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tab 3 – Sistema ──────────────────────────────────────────────────────────

const TabSistema: React.FC = () => {
  const [serverStatus, setServerStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [pendingCount, setPendingCount] = useState(0);
  const [animToggle, setAnimToggle] = useState(0); // forces re-render on toggle

  useEffect(() => {
    const q = JSON.parse(localStorage.getItem('softys_pending_sync') || '[]');
    setPendingCount(q.filter((r: any) => !r.synced).length);

    axios.get(`${REMOTE_API}/health`, { timeout: 3000 })
      .then(() => setServerStatus('online'))
      .catch(() => setServerStatus('offline'));
  }, []);

  const user = localStorage.getItem('isAuthenticated') || '–';

  const forceSyncNow = async () => {
    try {
      const q: any[] = JSON.parse(localStorage.getItem('softys_pending_sync') || '[]');
      const pending = q.filter(r => !r.synced);
      let synced = 0;
      for (const record of pending) {
        try {
          await axios.post(`${REMOTE_API}/v1/subscription`, record.data, { timeout: 5000 });
          record.synced = true;
          record.syncedAt = Date.now();
          synced++;
        } catch {}
      }
      localStorage.setItem('softys_pending_sync', JSON.stringify(q));
      setPendingCount(q.filter(r => !r.synced).length);
      alert(`Sync concluído: ${synced} registro(s) enviados.`);
    } catch (e) {
      alert('Erro ao sincronizar.');
    }
  };

  const clearSyncHistory = () => {
    if (window.confirm('Limpar histórico de sync (vai manter apenas pendentes)?')) {
      const q: any[] = JSON.parse(localStorage.getItem('softys_pending_sync') || '[]');
      const pending = q.filter(r => !r.synced);
      localStorage.setItem('softys_pending_sync', JSON.stringify(pending));
      setPendingCount(pending.length);
      alert('Histórico limpo.');
    }
  };

  return (
    <div>
      {/* Server */}
      <div style={S.sectionTitle}>🖥 Servidor Backend</div>
      <div style={S.card}>
        <div style={S.infoRow}>
          <span style={{ fontSize: '28px' }}>Endereço</span>
          <span style={{ fontSize: '28px', color: '#aab' }}>{REMOTE_API}</span>
        </div>
        <div style={S.infoRow}>
          <span style={{ fontSize: '28px' }}>Status</span>
          <span style={{ fontSize: '28px', fontWeight: 'bold', color: serverStatus === 'online' ? '#27ae60' : serverStatus === 'offline' ? '#e74c3c' : '#f39c12' }}>
            {serverStatus === 'online' ? '● Online' : serverStatus === 'offline' ? '● Offline' : '● Verificando...'}
          </span>
        </div>
        <div style={{ ...S.infoRow, borderBottom: 'none' }}>
          <span style={{ fontSize: '28px' }}>Registros pendentes de sync</span>
          <span style={{ fontSize: '32px', fontWeight: 'bold', color: pendingCount > 0 ? '#e67e22' : '#27ae60' }}>
            {pendingCount}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '16px', marginBottom: '36px', flexWrap: 'wrap' }}>
        <button style={S.btn('#5b9bd5')} onClick={forceSyncNow}>⚡ Forçar sync agora</button>
        <button style={S.btn('#666')} onClick={() => requirePin(clearSyncHistory)}>🗑 Limpar histórico sync</button>
      </div>

      {/* Session */}
      <div style={S.sectionTitle}>👤 Sessão Atual</div>
      <div style={S.card}>
        <div style={S.infoRow}>
          <span style={{ fontSize: '28px' }}>Usuário logado</span>
          <span style={{ fontSize: '28px', color: '#5b9bd5', fontWeight: 'bold' }}>{user}</span>
        </div>
        <div style={{ ...S.infoRow, borderBottom: 'none' }}>
          <span style={{ fontSize: '28px' }}>Data/hora</span>
          <span style={{ fontSize: '28px', color: '#aab' }}>{new Date().toLocaleString('pt-BR')}</span>
        </div>
      </div>

      {/* ─── Animation controls ─────────────────────────────────────────── */}
      <div style={S.sectionTitle}>⚙ Efeitos e Animações — Tela Inicial</div>
      <div style={S.card}>
        <div style={{ fontSize: '24px', color: '#7799bb', marginBottom: '24px', lineHeight: 1.5 }}>
          As alterações são aplicadas na próxima vez que a tela inicial for carregada.
        </div>

        {(([
          { key: 'typewriter',  label: '⌨️  Máquina de escrever',  desc: 'Slogan aparece letra a letra com cursor piscante; repete a cada 1 minuto.' },
          { key: 'logoReveal',  label: '🖼️  Surgimento da logo',   desc: 'Logo desliza da esquerda para direita ao iniciar a animação do texto.' },
          { key: 'qrPulse',    label: '📱  Pulsação do QR Code',   desc: 'QR Code dispara escala + brilho azul para chamar atenção a cada 15 segundos.' },
          { key: 'textEffects',label: '✨  Efeitos nos textos',    desc: '"Siga no Instagram" brilha em branco; "Toque para participar" pulsa suavemente.' },
        ]) as { key: keyof AnimConfig; label: string; desc: string }[]).map(item => {
          void animToggle; // keep reference so re-renders on toggle
          const cfg = loadAdminAnimConfig();
          const enabled = cfg[item.key];
          const toggle = () => {
            localStorage.setItem(ANIM_KEY, JSON.stringify({ ...cfg, [item.key]: !enabled }));
            setAnimToggle(t => t + 1);
          };
          return (
            <div key={item.key} style={{ padding: '20px 0', borderBottom: '1px solid #2a3a50' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '28px', fontWeight: 'bold' }}>{item.label}</span>
                {/* Toggle switch */}
                <div
                  onClick={toggle}
                  role="switch"
                  aria-checked={enabled}
                  style={{
                    width: '92px', height: '48px', borderRadius: '24px', cursor: 'pointer',
                    backgroundColor: enabled ? '#27ae60' : '#4a5568',
                    position: 'relative', transition: 'background 0.25s', flexShrink: 0,
                  }}
                >
                  <div style={{
                    position: 'absolute', top: '6px',
                    left: enabled ? '50px' : '6px',
                    width: '36px', height: '36px', borderRadius: '50%',
                    backgroundColor: '#fff', transition: 'left 0.25s',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                  }} />
                </div>
              </div>
              <div style={{ fontSize: '22px', color: '#7799bb', lineHeight: 1.5 }}>{item.desc}</div>
            </div>
          );
        })}
      </div>

      {/* App info */}
      <div style={S.sectionTitle}>ℹ Informações do App</div>
      <div style={S.card}>
        {[
          ['Evento', 'EXPODIRETO – SÃO JOÃO 2026'],
          ['Cliente', 'Softys'],
          ['Período', '06/03/2026 – 13/03/2026'],
          ['Horário', '08:00 – 20:00'],
          ['Resolução', '1080 × 1920 (totem vertical 55")'],
          ['Versão App', '1.0.0'],
        ].map(([label, value]) => (
          <div key={label} style={S.infoRow}>
            <span style={{ fontSize: '26px', color: '#aab' }}>{label}</span>
            <span style={{ fontSize: '26px' }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Main AdminPanel ──────────────────────────────────────────────────────────

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('cadastros');

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.headerTitle}>⚙ Painel Administrativo — Softys</div>
        <button style={S.backBtn} onClick={() => navigate('/presentation')}>← Voltar</button>
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        {([
          ['cadastros', '📋  Cadastros'],
          ['roleta',    '🎡  Roleta'],
          ['sistema',   '⚙  Sistema'],
        ] as [Tab, string][]).map(([key, label]) => (
          <button key={key} style={S.tab(tab === key)} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={S.content}>
        {tab === 'cadastros' && <TabCadastros />}
        {tab === 'roleta'    && <TabRoleta />}
        {tab === 'sistema'   && <TabSistema />}
      </div>
    </div>
  );
};

export default AdminPanel;
